import CryptoJS from 'crypto-js';
import { Translation } from '@app/api/translation.api';
import { NotificationType } from '@app/components/common/Notification/Notification';
import { Dates } from '@app/constants/Dates';
import { DealRoomPeopleLogAction } from '@app/constants/enums/deal-room-people-log';
import { RoutesMapping, RouteWisePermission } from '@app/constants/enums/permission';
import { httpStatusCodes } from '@app/constants/httpStatusCode';
import { DATE_TIME_FORMAT, DATE_TIME_FORMAT_DB, UNIQUE_TAB_ID } from '@app/constants/variables';
import { IChat } from '@app/interfaces/chat';
import { IDealRoom, IDealRoomMessage } from '@app/interfaces/deal-room';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import { IDealRoomPeopleLog } from '@app/interfaces/deal-room-people-log';
import { IFile } from '@app/interfaces/file';
import { ANY_OBJECT, PermissionData } from '@app/interfaces/interfaces';
import { IMessage } from '@app/interfaces/message';
import { IUser } from '@app/interfaces/user';
import defaultTranslation from '@app/locales/en/translation.json';
import { deleteToken } from '@app/services/localStorage.service';
import { DirectionType } from 'antd/lib/config-provider';
import { RcFile, UploadFile } from 'antd/lib/upload';
import atob from 'atob';
import { flatMap, isObject } from 'lodash';
import mime from 'mime-types';
import { TFunction } from 'react-i18next';
import { NavigateFunction, type Location, type Params } from 'react-router-dom';

const ENCRYPTION_SECRET_KEY = process.env.REACT_APP_ENCRYPTION_SECRET_KEY || '';

export const capitalize = (word: string): string => `${word ? `${word[0].toUpperCase()}${word.slice(1)}` : ''}`;

export const hexToRGB = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const msToH = (ms: number): number => Math.floor(ms / 3600000);

export const hToMS = (h: number): number => h * 3600000;

// find the http status severity and handle the case
export const checkHTTPStatus = (status: number, navigate: NavigateFunction): void => {
  const checkHTTPStatus = httpStatusCodes.find((item) => item.code === status);

  switch (checkHTTPStatus?.code) {
    case 400:
    case 401:
    case 500:
    case 502:
    case 511:
      deleteToken();
      navigate('/logout');
      break;

    default:
      break;
  }
};

export const contentDirection = (isRtl: boolean): DirectionType => {
  return isRtl ? 'rtl' : 'ltr';
};

export const getDefaultTranslationRow = (): Translation => ({
  name: 'english',
  isoCode: 'en',
  flagCode: 'gb',
  localHelper: 'en_US',
  isDeleted: false,
  isRtl: true,
  translations: defaultTranslation,
});

export const getAccessCode = (obj: PermissionData, value: string): number => {
  const checkAccess = Object.keys(obj).find((key: string) => key === value);
  return Number(checkAccess && obj[checkAccess]) || 0;
};

const getRoutePermissionAccessCode = (obj: PermissionData, routeObj: PermissionData, pathname: string): number => {
  const matchedValue = Object.values(routeObj).find((value) => value === pathname);
  if (matchedValue) {
    const matchedKey = Object.keys(obj).find((key: string) => routeObj[key] === matchedValue);
    if (matchedKey) {
      return Number(obj[matchedKey]);
    }
  }
  return 0;
};

export const sortAscendingDescending = (a: string, b: string): number => {
  return a.localeCompare(b, undefined, { numeric: true });
};

export const getSchema = (val: ANY_OBJECT, keys: unknown[] = []): string | unknown[] => {
  return isObject(val) ? flatMap(val, (v, k) => getSchema(v, [...keys, k]) as string | unknown[]) : keys.join('.');
};

export const randomString = (length: number): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let password = '';
  for (let i = 0; i < length; i++) {
    password += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/gm;
  return regex.test(password) ? password : randomString(length);
};

export const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const normFile = (e = { fileList: [] }): File[] => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

export const jsonToFormData = (data: ANY_OBJECT): FormData => {
  const formData = new FormData();
  Object.keys(data).forEach((key: string) => {
    if (Array.isArray(data[key])) {
      data[key].forEach((item: UploadFile, index: number) => {
        formData.append(`file${index}`, item.originFileObj as File);
      });
    } else {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

export const decodeHtmlEntities = (encodedString: string): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(encodedString, 'text/html');
  return doc.documentElement.textContent;
};

export const htmlConverter = (html: string): string => decodeHtmlEntities(html) || '';

/**
 * Function converts path like /user/123 to /user/:id
 */
export const getRoutePath = (location: Location, params: Params): string => {
  const { pathname } = location;

  if (!Object.keys(params).length) {
    return pathname; // we don't need to replace anything
  }

  let path = pathname;
  Object.entries(params).forEach(([paramName, paramValue]) => {
    if (paramValue) {
      path = path.replace(paramValue, `:${paramName}`);
    }
  });
  return path;
};

export const getPermissionType = (location: Location, params: Params, userPermission: PermissionData): number => {
  const path = getRoutePath(location, params);
  const permission = RouteWisePermission[path as keyof typeof RouteWisePermission];
  const checkPermission = getRoutePermissionAccessCode(userPermission, RoutesMapping, permission);
  return checkPermission;
};

/**
 * Function to get the user base slug
 */
export const getUserRouteInfo = (
  location: Location,
): {
  isAdmin: boolean;
  isClient: boolean;
  baseSlug: string;
} => {
  const isAdmin = location.pathname.includes('/admins');
  const isClient = location.pathname.includes('/workers');
  const baseSlug = isAdmin ? 'admins' : 'workers';
  return {
    isAdmin,
    isClient,
    baseSlug,
  };
};

/**
 * Function to convert milliseconds to time with string padding
 * @param milliseconds - number
 * @returns string
 */
export const convertMilliSecondsToTime = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

/**
 * Function to get the user full name
 * @param user - IUser | undefined | null
 * @returns string
 */
export const getUserFullName = (user: IUser | undefined | null): string => {
  return user ? `${user?.firstname || ''} ${user?.lastname || ''}`?.trim() : '';
};

/**
 * Function to get the people full name info
 * @param {IDealRoomPeople | undefined | null} user
 * @returns {string}
 */
export const getPeopleFullName = (user: IDealRoomPeople | undefined | null): string => {
  return user ? `${user?.firstName || ''} ${user?.lastName || ''}`?.trim() : '';
};

/**
 * Function to get the people full name info
 * @param {IDealRoomPeople | undefined | null} user
 * @returns {string}
 */
export const getPeopleFullNameInfo = (user: IDealRoomPeople | undefined | null): string => {
  return user ? `${user?.firstName}${user?.lastName ? ` ${user?.lastName}` : ''} (${user?.phone})` : '';
};

/**
 * Function to get the extension of the file
 * @param filename - string
 * @returns string
 */
export const getExtension = (filename: string): string => {
  return filename.split('.').pop() || '';
};

/**
 * Convert bytes to size
 * @param bytes - number
 * @param decimals - number
 * @returns string
 */
export const convertBytesToSize = (bytes: number, decimals = 2): string => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

/**
 * Download file from base64
 * @param base64 - string
 * @param name - string
 * @returns void
 */
export const downloadFileFromBase64 = (base64: string, name: string): void => {
  const downloadLink = document.createElement('a');
  downloadLink.href = `data:${mime.lookup(name)};base64,${base64}`;
  downloadLink.download = name;
  downloadLink.click();
  downloadLink.remove();
};

/**
 * Function to open the link in new tab
 * @param newWindow - Window | null
 * @param link - string | null
 * @returns void
 */
export const openLinkInNewTab = (newWindow: Window | null, link: string | null): void => {
  if (newWindow && link) newWindow.location.href = link;
};

const getViewMessage = (
  t: TFunction<'translation', undefined>,
  options: { deal: string; user: string },
  sharedBy?: IDealRoomPeople,
): string => {
  if (sharedBy) {
    return t('dealRooms.signals.viewSharedText', {
      ...options,
      sharedBy: (`${sharedBy?.firstName || ''} ${sharedBy?.lastName || ''}` || '')?.trim(),
    });
  }
  return t('dealRooms.signals.viewText', options);
};

const getActionMessage = (
  t: TFunction<'translation', undefined>,
  messageKey: string,
  options: { deal: string; user: string; file: string; comment: string },
): string => {
  return t(messageKey, options);
};

const getActionHandler = (
  action: DealRoomPeopleLogAction,
  t: TFunction<'translation', undefined>,
  options: { deal: string; user: string; comment: string; file: string },
  sharedBy?: IDealRoomPeople,
): string => {
  switch (action) {
    case DealRoomPeopleLogAction.VIEW:
      return getViewMessage(t, options, sharedBy);
    case DealRoomPeopleLogAction.OPEN_FILE:
      return getActionMessage(t, 'dealRooms.signals.openFileText', options);
    case DealRoomPeopleLogAction.DOWNLOAD:
      return getActionMessage(t, 'dealRooms.signals.downloadText', options);
    case DealRoomPeopleLogAction.UPLOAD:
      return getActionMessage(t, 'dealRooms.signals.uploadText', options);
    case DealRoomPeopleLogAction.UPDATE_UPLOAD:
      return getActionMessage(t, 'dealRooms.signals.updateUploadText', options);
    case DealRoomPeopleLogAction.LINK:
      return getActionMessage(t, 'dealRooms.signals.linkText', options);
    case DealRoomPeopleLogAction.UPDATE_LINK:
      return getActionMessage(t, 'dealRooms.signals.updateLinkText', options);
    case DealRoomPeopleLogAction.OPEN_LINK:
      return getActionMessage(t, 'dealRooms.signals.openLinkText', options);
    case DealRoomPeopleLogAction.ADD_COMMENT:
      return getActionMessage(t, 'dealRooms.signals.commentText', options);
    case DealRoomPeopleLogAction.ADD_COMMENT_FILE:
      return getActionMessage(t, 'dealRooms.signals.commentFileText', options);
    case DealRoomPeopleLogAction.DELETE_COMMENT:
      return getActionMessage(
        t,
        options.comment ? 'dealRooms.signals.deleteCommentText' : 'dealRooms.signals.deleteCommentFileText',
        options,
      );
    default:
      return '';
  }
};

/**
 * Function to get the deal room people log message
 * @param dealRoomPeopleLog - IDealRoomPeopleLog
 * @param t - TFunction<'translation', undefined>
 * @returns string
 */
export const getDealRoomPeopleLogMessage = (
  dealRoomPeopleLog: IDealRoomPeopleLog,
  t: TFunction<'translation', undefined>,
): string => {
  const { action, sharedBy, dealRoom, dealRoomPeople, file, description, link } = dealRoomPeopleLog;
  const options = {
    deal: (dealRoom as IDealRoom)?.title || '',
    user: (
      `${(dealRoomPeople as IDealRoomPeople)?.firstName || ''} ${
        (dealRoomPeople as IDealRoomPeople)?.lastName || ''
      }` || ''
    )?.trim(),
    comment: description || '',
    file: (file as IFile)?.originalName ? getReadableFileName((file as IFile)?.originalName as string) : '',
    link: link || '',
  };

  return getActionHandler(action, t, options, sharedBy as IDealRoomPeople);
};

const getUserDetails = (user: IUser | null, addRole = false) => {
  if (!user) return { id: '', name: '', image: '' };
  const role = user?.role?.role?.substr(0, 1) || '';
  return {
    id: user.id,
    name: `${getUserFullName(user)}${addRole ? ` [${role}]` : ''}`,
    image: user.image as unknown as string,
  };
};

const getPeopleDetails = (people: IDealRoomPeople | null) => {
  if (!people) return { id: '', name: '', image: '' };
  return {
    id: people.id,
    name: (`${people?.firstName || ''} ${people?.lastName || ''}` || '')?.trim(),
    image: '',
  };
};

const getSenderDetails = (chat: IChat, addRole: boolean) => {
  const { sendBy, sendByPeople } = chat;
  const userDetails = getUserDetails(sendBy ? (sendBy as IUser) : null, addRole);
  const peopleDetails = getPeopleDetails(sendByPeople ? (sendByPeople as IDealRoomPeople) : null);

  return {
    senderId: userDetails.id || peopleDetails.id,
    senderName: userDetails.name || peopleDetails.name,
    senderImage: userDetails.image || peopleDetails.image,
  };
};

/**
 * Function to map chat to message
 * @param {IChat} chat - IChat
 * @param {boolean} isDeleteOnly - boolean
 * @param {boolean} addRole - boolean
 * @returns {IMessage} IMessage
 */
export const mapChatToMessage = (chat: IChat, isDeleteOnly: boolean, addRole: boolean, user = ''): IMessage => {
  const senderDetails = getSenderDetails(chat, addRole);
  return {
    id: chat.id,
    message: chat.message || '',
    file: chat?.file || null,
    link: chat?.link || null,
    sender: senderDetails.senderId,
    senderName: senderDetails.senderName,
    senderImage: senderDetails.senderImage,
    isOwn: user ? senderDetails.senderId === user : false,
    createdAt: Dates.format(chat.createdAt, DATE_TIME_FORMAT),
    time: chat.createdAt,
    isDeleteOnly,
    isSentAdmin: !!chat.sendBy,
    dir: getTextDirection(chat.message || ''),
  };
};

/**
 * Get readable file name
 * @param {string} fileName - file name
 * @returns {string} readable file name
 */
export const getReadableFileName = (fileName: string): string => {
  // Step 1: Convert the string (assumed wrongly decoded from UTF-8) back to its original byte values
  const buffer = Buffer.from(fileName, 'latin1');

  // Step 2: Interpret those bytes properly as UTF-8
  return buffer.toString('utf8');
};

export const firstLatterOfName = (name: string): string => {
  if (!name) return '';
  const nameParts = name.trim().split(' ');
  const firstLetter = nameParts[0] ? nameParts[0][0].toUpperCase() : '';
  const secondLetter = nameParts[1] ? nameParts[1][0].toUpperCase() : '';
  return firstLetter + secondLetter;
};

export const getDealRoomBuildingText = (dealRoom: IDealRoom): string | null => {
  if (!dealRoom?.buildings || !dealRoom.buildings?.length) return null;
  const buildings = dealRoom.buildings[0];
  const firstCity = buildings?.city || '';
  return dealRoom.buildings && dealRoom.buildings.length > 0
    ? `${dealRoom.buildings
        .map(
          (addr) =>
            `${addr.street} ${addr.houseNumber}${addr.toHouseNumber ? `-${addr.toHouseNumber}` : ''}${
              addr.entrance ? `(${addr.entrance})` : ''
            }`,
        )
        .join(', ')}, ${firstCity}`
    : null;
};

export const getDealRoomFirstBuildingText = (dealRoom: IDealRoom): string | null => {
  if (!dealRoom?.buildings || !dealRoom.buildings?.length) return null;
  const building = dealRoom.buildings[0];
  return `${`${building.street} ${building.houseNumber}${building.toHouseNumber ? `-${building.toHouseNumber}` : ''}${
    building.entrance ? `(${building.entrance})` : ''
  }`}, ${building.city}`;
};

export const getTabId = (): string => {
  let tabId = sessionStorage.getItem(UNIQUE_TAB_ID);

  // if duplicated, regenerate
  if (!tabId || window.name === '') {
    tabId = crypto.randomUUID();
    sessionStorage.setItem(UNIQUE_TAB_ID, tabId);
    window.name = tabId; // survive reload
  }

  return tabId;
};

export const getRawMessage = (
  message: string,
): {
  __html: string;
} => {
  const updatedHtml = message.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noreferrer">$1</a>');
  return { __html: updatedHtml };
};

export const getTextDirection = (str: string): string => {
  if (!str) return 'rtl';

  const rtlPattern = /[\u0590-\u05FF\u0600-\u06FF]/;

  return rtlPattern.test(str) ? 'rtl' : 'ltr';
};

export const encryptField = (value: string): string => {
  return CryptoJS.AES.encrypt(value?.toString(), ENCRYPTION_SECRET_KEY).toString();
};

export const decryptField = (encryptedValue: string): string => {
  const bytes = CryptoJS.AES.decrypt(encryptedValue?.toString(), ENCRYPTION_SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
};

export const canEditOrDeleteMessage = (message: IMessage, user: IUser | null): boolean => {
  const { isOwn, time } = message;
  if (!isOwn) return false;
  const formattedDate = Dates.format(time, DATE_TIME_FORMAT_DB);
  const messageDate = Dates.getDate(formattedDate);
  const now = Dates.getToday();
  const minutes = now.diff(messageDate, 'minute');
  const validateMinutes = user
    ? Number(process.env.REACT_APP_ADMIN_CHAT_EDITABLE_TIME || 0)
    : Number(process.env.REACT_APP_CLIENT_CHAT_EDITABLE_TIME || 0);
  return minutes <= validateMinutes;
};

export const getAdminNotificationMessageKeyOnChat = (data: IDealRoomMessage): string => {
  if (data?.isDelete) {
    return 'dealRooms.signals.deleteMessageInChatText';
  }
  if (data?.isEdit) {
    return 'dealRooms.signals.updateMessageInChatText';
  }

  return 'dealRooms.signals.messageInChatText';
};
